import { newUuid } from '@overlayqa/shared';
import { useEffect, useState } from 'react';
import { useAuth, useUser } from '../lib/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { fetchApi } from '../lib/api/client';
import { captureOAuthHandoff, clearOAuthHandoff, readOAuthHandoff } from '../lib/oauthLandingHandoff';

const providers = new Set(['jira', 'linear', 'notion', 'asana', 'trello', 'clickup', 'github', 'basecamp', 'monday', 'slack', 'figma']);
const requests = new Map<string, Promise<void>>();

export function useOAuthFinalize(provider: string, callbackError: string | null, legacySuccess: boolean) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const userId = user?.id;
  const [handle] = useState(() => {
    captureOAuthHandoff();
    return callbackError ? null : readOAuthHandoff(provider);
  });
  const [state, setState] = useState<{ status: 'pending' | 'success' | 'error'; message?: string }>({
    status: handle && providers.has(provider) ? 'pending' : legacySuccess && !callbackError ? 'success' : 'error',
  });

  useEffect(() => {
    if (!handle || !isLoaded || !providers.has(provider)) return;
    if (isSignedIn && !userId) return;
    let active = true;
    const finish = (message?: string) => {
      if (!active) return;
      setState(message ? { status: 'error', message } : { status: 'success' });
      if (!message) clearOAuthHandoff(provider);
    };
    if (isSignedIn) {
      // Clerk owns the identity when present. Never fall back to a different
      // extension account after a mismatch or an unsuccessful dashboard request.
      const key = `${provider}:${handle}:${userId}`;
      let request = requests.get(key);
      if (!request) {
        request = (async () => {
          const token = await getToken();
          if (!token) throw new Error('Sign in again to finish connecting.');
          const endpoint = provider === 'figma' ? '/figma/oauth/finalize' : `/integrations/${provider}/finalize`;
          await fetchApi(endpoint, { method: 'POST', token, body: JSON.stringify({ handle }) });
        })();
        requests.set(key, request);
      }
      void request.then(() => finish(), (error: unknown) => finish(error instanceof Error ? error.message : 'Could not finish connecting. Please start again.'));
      return () => { active = false; };
    }

    const requestId = newUuid();
    const receive = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.type !== 'OVERLAYQA_OAUTH_FINALIZED' || event.data.requestId !== requestId) return;
      clearInterval(interval);
      finish(event.data.success === true ? undefined : event.data.error || 'Sign in to finish connecting.');
    };
    const send = () => window.postMessage({
      type: 'OVERLAYQA_FINALIZE_OAUTH', requestId, provider, handle, apiOrigin: apiBaseUrl(),
    }, window.location.origin);
    window.addEventListener('message', receive);
    // Retry the handshake because the content script loads at document_idle.
    // No new network request is made for repeats: the extension deduplicates it.
    const interval = window.setInterval(send, 1000);
    send();
    return () => { active = false; clearInterval(interval); window.removeEventListener('message', receive); };
  }, [handle, provider, isLoaded, isSignedIn, userId, getToken]);

  return {
    ...state,
    canSignIn: isLoaded && !isSignedIn && !!handle,
    signInHref: `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`,
  };
}
